import { TpvPage } from '@app/domain/models/tpv/tpv-page.model';
import { TpvEnvelope, unwrapTpv } from '@app/data/tpv/tpv-envelope';

/** Forma típica de una respuesta de lista paginada del backend. */
interface RawListShape<TDto> {
  data?: TDto[];
  total?: number;
  page?: number;
  limit?: number;
}

/**
 * Normaliza la respuesta de una lectura paginada a `TpvPage<TDomain>`,
 * tolerando dos formas del backend (contrato exacto a confirmar, TPV_MIGRATION §9):
 *  - `{ data: TDto[], total, page, limit }`
 *  - `TDto[]` pelado
 *
 * `unwrapTpv` ya desenvuelve el envelope `{code,status,message,data}`.
 *
 * Modo `getAll`: cuando la lectura se hace con `get_all=true` (workaround del
 * micro TPV), la respuesta ES el conjunto completo ya scopeado al usuario. En ese
 * caso NO se arma paginación y se IGNORA el campo `total` del body (viene inflado,
 * es el del micro): el conteo real es `data.length` (= `extra_data.data.length`).
 */
export function toTpvPage<TDto, TDomain>(
  raw: TpvEnvelope<unknown> | unknown,
  mapItem: (dto: TDto) => TDomain,
  fallback: { page: number; limit: number; getAll?: boolean },
): TpvPage<TDomain> {
  const body = unwrapTpv(raw as TpvEnvelope<RawListShape<TDto> | TDto[]>) as
    | RawListShape<TDto>
    | TDto[];

  const items: TDto[] = Array.isArray(body) ? body : body?.data ?? [];

  if (fallback.getAll) {
    return { data: items.map(mapItem), total: items.length, page: 1, limit: items.length };
  }

  const total = Array.isArray(body) ? items.length : body?.total ?? items.length;
  const page = Array.isArray(body) ? fallback.page : body?.page ?? fallback.page;
  const limit = Array.isArray(body) ? fallback.limit : body?.limit ?? fallback.limit;

  return { data: items.map(mapItem), total, page, limit };
}
